import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Sun, Moon, LogOut, LayoutDashboard, Settings, HelpCircle,
  Menu, History, Users, ArrowUpRight, ArrowDownLeft,
  Search, Filter, Calendar, TrendingUp, TrendingDown,
  Wallet, X, Download, Maximize2, User, CreditCard, Phone, Mail, Target, Zap, Activity
} from 'lucide-react';
import { api } from '../api';
import './DashboardPage.css';
import './HistoryPage.css';

const LOGO_URL = 'https://acesso.ihubs.app/api/uploads/logo/acesso/logo-light-1773085624457.png';

const formatCurrency = (value) =>
  Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (date) =>
  new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

const formatTime = (date) =>
  new Date(date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const formatDateFull = (date) =>
  new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

const formatCpf = (cpf) =>
  (cpf || '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');

const formatPhone = (phone) => {
  const p = (phone || '').replace(/\D/g, '');
  if (p.length <= 2) return p;
  if (p.length <= 7) return `(${p.slice(0,2)}) ${p.slice(2)}`;
  return `(${p.slice(0,2)}) ${p.slice(2,7)}-${p.slice(7)}`;
};

function HistoryPage({ user, onLogout, theme, setTheme, setPage }) {
  const [transactions, setTransactions] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const txs = await api.getTransactions();
      setTransactions(txs);
    } catch {}
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const initials = (user.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const isAdmin = user.role === 'admin' || user.role === 'supervisor';

  // Filter logic
  const filtered = useMemo(() => {
    let result = [...transactions];

    // Type filter
    if (typeFilter !== 'all') {
      result = result.filter(t => t.type === typeFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      let startDate;
      if (dateFilter === '7d') {
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      } else if (dateFilter === '30d') {
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      } else if (dateFilter === '90d') {
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      } else if (dateFilter === '12m') {
        startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      }
      if (startDate) {
        result = result.filter(t => new Date(t.created_at) >= startDate);
      }
    }

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(t =>
        (t.description || '').toLowerCase().includes(q) ||
        (t.user_name || '').toLowerCase().includes(q) ||
        formatCurrency(t.amount).includes(q)
      );
    }

    return result;
  }, [transactions, typeFilter, dateFilter, search]);

  // Summary stats
  const stats = useMemo(() => {
    const deposits = filtered.filter(t => t.type === 'deposit');
    const withdrawals = filtered.filter(t => t.type === 'withdraw');
    const totalDeposits = deposits.reduce((s, t) => s + Number(t.amount), 0);
    const totalWithdrawals = withdrawals.reduce((s, t) => s + Number(t.amount), 0);
    return {
      total: filtered.length,
      deposits: deposits.length,
      withdrawals: withdrawals.length,
      totalDeposits,
      totalWithdrawals,
      net: totalDeposits - totalWithdrawals,
    };
  }, [filtered]);

  // Group by date
  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach(t => {
      const dateKey = formatDate(t.created_at);
      if (!groups[dateKey]) groups[dateKey] = { date: t.created_at, items: [] };
      groups[dateKey].items.push(t);
    });
    return Object.values(groups);
  }, [filtered]);

  const activeFiltersCount = [typeFilter !== 'all', dateFilter !== 'all'].filter(Boolean).length;

  return (
    <div className={`dashboard-container ${theme}`}>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-top">
          <div className="sidebar-logo">
            <img src={LOGO_URL} alt="Logo" className="sidebar-logo-img" />
          </div>
          <nav className="sidebar-nav">
            <a href="#dashboard" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('dashboard'); }}>
              <LayoutDashboard size={19} /><span>Dashboard</span>
            </a>
            {isAdmin && (
              <a href="#admin" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('admin'); }}>
                <Users size={19} /><span>Admin</span>
              </a>
            )}
            {isAdmin && (
              <a href="#automation" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('automation'); }}>
                <Zap size={19} /><span>Automacao IA</span>
              </a>
            )}
            {isAdmin && (
              <a href="#activity" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('activity'); }}>
                <Activity size={19} /><span>Atividade</span>
              </a>
            )}
            <a href="#history" className="nav-item active" onClick={() => setSidebarOpen(false)}>
              <History size={19} /><span>Histórico</span>
            </a>
            <a href="#settings" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('settings'); }}>
              <Settings size={19} /><span>Configurações</span>
            </a>
            <a href="#help" className="nav-item">
              <HelpCircle size={19} /><span>Ajuda</span>
            </a>
          </nav>
        </div>
        <div className="sidebar-bottom">
          <button className="sidebar-theme-btn" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            <span>{theme === 'dark' ? 'Tema claro' : 'Tema escuro'}</span>
          </button>
          <div className="sidebar-user">
            <div className="user-avatar">{initials}</div>
            <div className="user-info">
              <span className="user-name">{user.name}</span>
              <span className="user-role">{user.role}</span>
            </div>
            <button className="logout-btn-sidebar" onClick={onLogout} title="Sair"><LogOut size={16} /></button>
          </div>
        </div>
      </aside>

      <div className="main-wrapper">
        <header className="topbar">
          <button className="menu-btn" onClick={() => setSidebarOpen(true)}><Menu size={20} /></button>
          <img src={LOGO_URL} alt="Logo" className="topbar-logo" />
          <div className="topbar-right">
            <button className="topbar-theme" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <div className="user-avatar small">{initials}</div>
          </div>
        </header>

        <main className="dash-main">
          <div className="page-header">
            <div>
              <p className="page-greeting">Suas Transações</p>
              <h1 className="page-title">Histórico Completo</h1>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="stats-grid history-stats">
            <div className="stat-card">
              <div className="stat-icon green"><TrendingUp size={18} /></div>
              <div className="stat-content">
                <span className="stat-label">Total depósitos</span>
                <span className="stat-value">{formatCurrency(stats.totalDeposits)}</span>
              </div>
              <span className="stat-count-badge deposit">{stats.deposits}</span>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ background: 'rgba(239,68,68,0.1)', color: '#f87171' }}><TrendingDown size={18} /></div>
              <div className="stat-content">
                <span className="stat-label">Total saques</span>
                <span className="stat-value">{formatCurrency(stats.totalWithdrawals)}</span>
              </div>
              <span className="stat-count-badge withdraw">{stats.withdrawals}</span>
            </div>
            <div className="stat-card">
              <div className="stat-icon blue"><Wallet size={18} /></div>
              <div className="stat-content">
                <span className="stat-label">Saldo líquido</span>
                <span className="stat-value">{formatCurrency(stats.net)}</span>
              </div>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="history-toolbar">
            <div className="history-search">
              <Search size={16} className="search-icon" />
              <input
                type="text" placeholder="Buscar transações..."
                value={search} onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button className="search-clear" onClick={() => setSearch('')}><X size={14} /></button>
              )}
            </div>
            <button
              className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={15} />
              <span>Filtros</span>
              {activeFiltersCount > 0 && <span className="filter-count">{activeFiltersCount}</span>}
            </button>
          </div>

          {showFilters && (
            <div className="filters-panel">
              <div className="filter-group">
                <label><Calendar size={13} /> Período</label>
                <div className="filter-chips">
                  {[
                    { value: 'all', label: 'Todos' },
                    { value: '7d', label: '7 dias' },
                    { value: '30d', label: '30 dias' },
                    { value: '90d', label: '90 dias' },
                    { value: '12m', label: '12 meses' },
                  ].map(opt => (
                    <button key={opt.value}
                      className={`filter-chip ${dateFilter === opt.value ? 'active' : ''}`}
                      onClick={() => setDateFilter(opt.value)}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>
              <div className="filter-group">
                <label><Filter size={13} /> Tipo</label>
                <div className="filter-chips">
                  {[
                    { value: 'all', label: 'Todos' },
                    { value: 'deposit', label: 'Depósitos' },
                    { value: 'withdraw', label: 'Saques' },
                  ].map(opt => (
                    <button key={opt.value}
                      className={`filter-chip ${typeFilter === opt.value ? 'active' : ''}`}
                      onClick={() => setTypeFilter(opt.value)}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>
              {activeFiltersCount > 0 && (
                <button className="clear-filters-btn" onClick={() => { setTypeFilter('all'); setDateFilter('all'); }}>
                  <X size={13} /> Limpar filtros
                </button>
              )}
            </div>
          )}

          {/* Transaction List */}
          <div className="history-card">
            <div className="history-card-header">
              <h3 className="section-title"><History size={17} /> Transações</h3>
              <span className="history-count">{filtered.length} de {transactions.length} registro{transactions.length !== 1 && 's'}</span>
            </div>
            {filtered.length === 0 ? (
              <div className="empty-history">
                <History size={32} className="empty-icon" />
                <p>{search || typeFilter !== 'all' || dateFilter !== 'all'
                  ? 'Nenhuma transação encontrada com os filtros aplicados'
                  : 'Nenhuma transação realizada'
                }</p>
              </div>
            ) : (
              <div className="history-list">
                {grouped.map((group, gi) => (
                  <div key={gi}>
                    <div className="history-date-separator">
                      <span>{formatDateFull(group.date)}</span>
                    </div>
                    {group.items.map((item) => (
                      <div key={item.id} className={`history-item ${isAdmin ? 'clickable' : ''}`}
                        onClick={() => isAdmin && setDetailItem(item)}>
                        <div className={`history-icon ${item.type}`}>
                          {item.type === 'deposit' ? <ArrowDownLeft size={15} /> : <ArrowUpRight size={15} />}
                        </div>
                        <div className="history-info">
                          <span className="history-desc">{item.description || (item.type === 'deposit' ? 'Depósito' : 'Saque')}</span>
                          <span className="history-date">
                            {formatTime(item.created_at)}
                            {isAdmin && item.user_name && <> &middot; {item.user_name}</>}
                          </span>
                        </div>
                        <span className={`history-amount ${item.type}`}>
                          {item.type === 'deposit' ? '+' : '-'} {formatCurrency(item.amount)}
                        </span>
                        {isAdmin && <Maximize2 size={14} className="history-expand-icon" />}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Transaction Detail Modal */}
      {detailItem && (
        <div className="modal-overlay" onClick={() => setDetailItem(null)}>
          <div className="modal-card tx-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top-line" />
            <div className="modal-header">
              <div>
                <h3>Detalhes da Transação</h3>
                <p className="modal-subtitle">#{detailItem.id}</p>
              </div>
              <button className="modal-close" onClick={() => setDetailItem(null)}><X size={18} /></button>
            </div>

            <div className="tx-detail-content">
              {/* Transaction info */}
              <div className={`tx-detail-amount-card ${detailItem.type}`}>
                <div className={`tx-detail-type-icon ${detailItem.type}`}>
                  {detailItem.type === 'deposit' ? <ArrowDownLeft size={20} /> : <ArrowUpRight size={20} />}
                </div>
                <div className="tx-detail-amount-info">
                  <span className="tx-detail-type-label">{detailItem.type === 'deposit' ? 'Depósito' : 'Saque'}</span>
                  <span className={`tx-detail-amount-value ${detailItem.type}`}>
                    {detailItem.type === 'deposit' ? '+' : '-'} {formatCurrency(detailItem.amount)}
                  </span>
                </div>
              </div>

              <div className="tx-detail-row">
                <Calendar size={14} className="tx-detail-row-icon" />
                <span className="tx-detail-row-label">Data</span>
                <span className="tx-detail-row-value">{formatDateFull(detailItem.created_at)} às {formatTime(detailItem.created_at)}</span>
              </div>

              {detailItem.description && (
                <div className="tx-detail-row">
                  <History size={14} className="tx-detail-row-icon" />
                  <span className="tx-detail-row-label">Descrição</span>
                  <span className="tx-detail-row-value">{detailItem.description}</span>
                </div>
              )}

              {/* Client info */}
              {detailItem.user_name && (
                <>
                  <div className="tx-detail-separator">
                    <span>Dados do Cliente</span>
                  </div>

                  <div className="tx-detail-client-card">
                    <div className="tx-detail-client-header">
                      <div className="tx-detail-client-avatar">
                        {(detailItem.user_name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="tx-detail-client-name-wrap">
                        <span className="tx-detail-client-name">{detailItem.user_name}</span>
                        {detailItem.user_cpf && <span className="tx-detail-client-cpf">{formatCpf(detailItem.user_cpf)}</span>}
                      </div>
                    </div>

                    {detailItem.user_phone && (
                      <div className="tx-detail-row">
                        <Phone size={14} className="tx-detail-row-icon" />
                        <span className="tx-detail-row-label">Telefone</span>
                        <span className="tx-detail-row-value">{formatPhone(detailItem.user_phone)}</span>
                      </div>
                    )}

                    {detailItem.user_email && (
                      <div className="tx-detail-row">
                        <Mail size={14} className="tx-detail-row-icon" />
                        <span className="tx-detail-row-label">E-mail</span>
                        <span className="tx-detail-row-value">{detailItem.user_email}</span>
                      </div>
                    )}

                    {detailItem.user_balance !== undefined && (
                      <div className="tx-detail-row">
                        <Wallet size={14} className="tx-detail-row-icon" />
                        <span className="tx-detail-row-label">Saldo atual</span>
                        <span className="tx-detail-row-value">{formatCurrency(detailItem.user_balance)}</span>
                      </div>
                    )}

                    {detailItem.user_goal !== undefined && Number(detailItem.user_goal) > 0 && (
                      <div className="tx-detail-row">
                        <Target size={14} className="tx-detail-row-icon" />
                        <span className="tx-detail-row-label">Meta</span>
                        <span className="tx-detail-row-value">{formatCurrency(detailItem.user_goal)}</span>
                      </div>
                    )}

                    {detailItem.user_goal !== undefined && Number(detailItem.user_goal) > 0 && (
                      <div className="tx-detail-progress-wrap">
                        <div className="tx-detail-progress-bar">
                          <div className="tx-detail-progress-fill" style={{
                            width: `${Math.min((Number(detailItem.user_balance) / Number(detailItem.user_goal)) * 100, 100)}%`
                          }} />
                        </div>
                        <span className="tx-detail-progress-pct">
                          {Math.min((Number(detailItem.user_balance) / Number(detailItem.user_goal)) * 100, 100).toFixed(0)}%
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HistoryPage;
